/**
 * Gdzie mieszka widget i jakie adresy z tego wynikaja.
 *
 * Historia, ktora to uzasadnia (P3.32, dwie nieudane proby): panel USTALAL adres widgetu przez
 * sondowanie `localhost:4000`, `localhost:3002`, a na koncu domeny aplikacji. W dev to dziala, bo
 * widget faktycznie stoi na 4000. Na produkcji dwa pierwsze nie odpowiadaja, wiec zostawala domena
 * aplikacji i wypozyczalnia dostawala kod do wklejenia wskazujacy na serwer, na ktorym plikow
 * widgetu NIE MA i nigdy nie bedzie: obraz backendu budowany jest z kontekstu `Login/backend`, a
 * `widget/dist` lezy poza nim. Widget ma wlasny kontener i wlasna domene (`WIDGET_DOMAIN`), wiec
 * adres byl znany z konfiguracji przez caly czas — panel go tylko nie czytal.
 *
 * Stad podzial: jawna konfiguracja bije sondowanie, a sondowanie zostaje jako wygoda dev.
 *
 * Modul jest CZYSTY (bez React, bez fetch), zeby te regule dalo sie sprawdzic testem, a nie
 * dopiero na wdrozonej produkcji — co przy tym zadaniu zawiodlo juz dwa razy.
 */

/** Ucina koncowe ukosniki, zeby sklejanie adresow nie dawalo „//embed.js". */
const bezKoncowegoUkosnika = (u) => String(u || '').replace(/\/+$/, '');

/**
 * Czy skonfigurowana wartosc to SENSOWNY adres hosta. Zwraca adres albo '' (czyli „brak
 * konfiguracji", z powrotem do sondowania).
 *
 * Bez tego cala poprawka miala te sama wade co blad, ktory naprawia. `REACT_APP_WIDGET_HOST`
 * powstaje w compose przez sklejenie `https://` z `WIDGET_DOMAIN`, wiec:
 *  - brak zmiennej daje „https://" — po ucieciu ukosnikow „https:", wartosc PRAWDZIWA, wiec
 *    sondowanie bylo pomijane i panel wystawial `https:/embed.js`,
 *  - `WIDGET_DOMAIN` ze schematem daje „https://https://widget…", co `new URL` przyjmuje bez
 *    protestu (hostname „https", reszta jako sciezka).
 * Oba konczyly sie CICHO zepsutym kodem do wklejenia. Znalezione w code review PR #127.
 *
 * Regula jest waska celowo: host widgetu to zawsze samo pochodzenie, nigdy sciezka.
 */
export function poprawnyHost(u) {
  const s = bezKoncowegoUkosnika(u);
  return /^https?:\/\/[^/\s?#]+$/.test(s) ? s : '';
}

/**
 * Adres, pod ktorym stoi widget.
 *
 * @param {Object} p
 * @param {string} [p.skonfigurowany] REACT_APP_WIDGET_HOST — wartosc z wdrozenia
 * @param {string} [p.wykryty] wynik sondowania (dev)
 * @param {string} [p.apiBase] domena aplikacji; ostatnia deska ratunku
 * @returns {string} adres bez koncowego ukosnika ('' gdy nie wiadomo)
 */
export function wybierzHostWidgetu({ skonfigurowany, wykryty, apiBase } = {}) {
  // Konfiguracja liczy sie TYLKO gdy jest sensowna; smiec cofa nas do sondowania, zamiast
  // po cichu wygrywac (patrz poprawnyHost).
  return bezKoncowegoUkosnika(poprawnyHost(skonfigurowany) || wykryty || apiBase || '');
}

/**
 * Adresy budowane z hosta widgetu.
 *
 * Rozroznienie jest jedno: gdy widget serwuje SAM SIEBIE (wlasna domena albo dev na 4000), pliki
 * leza w korzeniu. Gdy hostem jest domena aplikacji, backend wystawia je pod `/widget/`. Ta druga
 * galaz dziala wylacznie lokalnie i zostaje po to, zeby dev bez osobnego kontenera dalej mial
 * podglad — na produkcji nie ma tam czego serwowac.
 *
 * @returns {{scriptSrc: string, pageBase: string, wKataloguWidget: boolean}}
 */
export function adresyWidgetu(host, apiBase) {
  const h = bezKoncowegoUkosnika(host);
  const wKataloguWidget = h !== '' && h === bezKoncowegoUkosnika(apiBase);
  const przedrostek = wKataloguWidget ? '/widget' : '';
  return {
    scriptSrc: `${h}${przedrostek}/embed.js`,
    pageBase: `${h}${przedrostek}/`,
    wKataloguWidget,
  };
}

/**
 * Czy w ogole sondowac. Jawna konfiguracja czyni sondowanie zbednym, a na produkcji wrecz
 * szkodliwym: `fetch` z `mode: 'no-cors'` spelnia sie takze dla odpowiedzi 404, wiec sonda
 * „potwierdzala" host, ktory zadnego widgetu nie serwuje.
 */
export function czySondowac(skonfigurowany) {
  return !poprawnyHost(skonfigurowany);
}

/**
 * Link do rezerwacji (F.4): strona widgetu otwarta wprost, bez strony WWW wypożyczalni (wysyłany
 * klientom albo drukowany jako kod QR). Te same parametry, które embed.js przekazuje ramce
 * (`widget`, `apiBase`, `token`), w tej samej kolejności, więc widget rozpoznaje firmę tak samo
 * jak osadzony. Caddy przekierowuje na stronę firmową wyłącznie adres BEZ identyfikatora
 * (Caddyfile, `@bezFirmy`), a ten link zawsze go niesie. Po płatności widget otwarty wprost wraca
 * na własny adres (widget/src/adresPowrotu.js).
 *
 * @returns {string} pełny adres albo '' (nieznany host albo brak identyfikatora firmy)
 */
export function linkRezerwacji({ host, apiBase, identyfikator } = {}) {
  const id = String(identyfikator || '').trim();
  const h = poprawnyHost(host);
  if (!id || !h) return '';
  const { pageBase } = adresyWidgetu(h, apiBase);
  const parametry = [`widget=${encodeURIComponent(id)}`];
  const api = bezKoncowegoUkosnika(apiBase);
  if (api) parametry.push(`apiBase=${encodeURIComponent(api)}`);
  parametry.push(`token=${encodeURIComponent(id)}`);
  return `${pageBase}?${parametry.join('&')}`;
}
